import React, { useEffect, useState } from 'react'
import { Table, Button, Modal, Input, Space, Select } from 'antd'
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SlackOutlined,
} from '@ant-design/icons'
import {
  CreateCustomerDataAPI,
  DeleteCustomerDataAPI,
  GetCityDataAPI,
  GetCustomerDataAPI,
  UpdateCustomerDataAPI,
} from '../../AllAPI/AdminAPI'
import axiosInstance from '../../Utils/axiosInstance'
import { toast } from 'react-toastify'

const CustomerMaster = () => {
  const selectedYear = Number(sessionStorage.getItem('year'))
  const [customers, setCustomers] = useState([])
  const [cities, setCities] = useState([])
  const [cityId, setCityId] = useState(null)
  const [searchText, setSearchText] = useState('')
  const [loading, setLoading] = useState(false)

  /* ================= Modal + Inputs ================= */
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingId, setEditingId] = useState(null)

  const [customerName, setCustomerName] = useState('')
  const [address, setAddress] = useState('')
  const [contactNo, setContactNo] = useState('')

  /* ================= Delete ================= */
  // const [deleteId, setDeleteId] = useState(null)

  useEffect(() => {
    fetchCustomers()
      fetchCities()
  }, [])

   const fetchCities = async () => {
    setLoading(true)
    try {
      const res = await axiosInstance.get(GetCityDataAPI)
      setCities(Array.isArray(res.data) ? res.data : [])
    } catch {
      toast.error('Failed to load cities')
    } finally {
      setLoading(false)
    }
  }

  /* ================= Fetch ================= */
  const fetchCustomers = async () => {
    setLoading(true)
    try {
      const res = await axiosInstance.get(`${GetCustomerDataAPI}/${selectedYear}`)
      setCustomers(Array.isArray(res.data) ? res.data : [])
    } catch {
      toast.error('Failed to load customers')
    } finally {
      setLoading(false)
    }
  }

  /* ================= Open Modal ================= */
  const openModal = (record = null) => {
    if (record) {
      setEditingId(record.id)
      setCityId(record.city_id)
      setCustomerName(record.customer_name)
      setAddress(record.address)
      setContactNo(record.contact)
    } else {
      setEditingId(null)
      setCustomerName('')
      setAddress('')
      setContactNo('')
    }
    setIsModalOpen(true)
  }

  /* ================= Save / Update ================= */
  const handleSave = async () => {
    if (!customerName.trim()) {
      return toast.error('Customer name is required')
    }

    if (!address.trim()) {
      return toast.error('Address is required')
    }

    if (!/^[0-9]{10}$/.test(contactNo)) {
      return toast.error('Enter valid 10-digit contact number')
    }

    const objData = {
      customer_name: customerName,
      address: address,
      city_id: cityId,
      contact: contactNo,
      year: selectedYear,
    }

    try {
      if (editingId) {
        const res = await axiosInstance.put(
          `${UpdateCustomerDataAPI}/${editingId}`,
          objData,
        )
        if (res.data === 'UPDATED') toast.success('Customer Updated')
      } else {
        const res = await axiosInstance.post(CreateCustomerDataAPI, objData)
        if (res.data === 'SAVED') toast.success('Customer Added')
      }

      fetchCustomers()
      setIsModalOpen(false)
      setEditingId(null)
      setCustomerName('')
      setCityId(null)
      setAddress('')
      setContactNo('')
    } catch {
      toast.error('Failed to save customer')
    }
  }

  /* ================= Delete ================= */
  // const confirmDelete = async () => {
  //   try {
  //     await axiosInstance.delete(`${DeleteCustomerDataAPI}/${deleteId}`)
  //     toast.success('Customer deleted')
  //     fetchCustomers()
  //   } catch {
  //     toast.error('Delete failed')
  //   } finally {
  //     setDeleteId(null)
  //   }
  // }

  /* ================= Search ================= */
  const filteredCustomers = customers.filter((c) =>
    c.customer_name?.toLowerCase().includes(searchText.toLowerCase()),
  )

  /* ================= Table ================= */
  const columns = [
    {
      title: 'Customer Name',
      dataIndex: 'customer_name',
    },
    {
      title: 'Address',
      dataIndex: 'address',
    },
      {
      title: 'City',
      dataIndex: ['city', 'city_name'], 
    },
    {
      title: 'Contact No',
      dataIndex: 'contact',
    },
    {
      title: 'Actions',
      render: (_, record) => (
        <Space>
          <Button
            icon={<EditOutlined />}
            size="small"
            onClick={() => openModal(record)}
          />
          {/* <Button
            icon={<DeleteOutlined />}
            size="small"
            danger
            onClick={() => setDeleteId(record.id)}
          /> */}
        </Space>
      ),
    },
  ]

  return (
    <>
      <div className="mainContainer">
        <div className="header-row">
          <h3>
            <SlackOutlined style={{ color: 'orangered' }} /> Customer Master
          </h3>
        </div>

        <div className="search-bar text-end">
          <Button
            className="addButton mx-md-2"
            icon={<PlusOutlined />}
            onClick={() => openModal()}
          >
            Add
          </Button>

          <Input
            placeholder="Search customer..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 250 }}
          />
        </div>

        <Table
          columns={columns}
          dataSource={filteredCustomers}
          rowKey="id"
          loading={loading}
          bordered
          className="shadow"
          style={{ overflowX: 'auto' }}
        />
      </div>

      {/* ================= MODAL ================= */}
      <Modal
        title={editingId ? 'Edit Customer' : 'Add Customer'}
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        footer={[
          <Button key="cancel" onClick={() => setIsModalOpen(false)}>
            Cancel
          </Button>,
          <Button key="save" type="primary" onClick={handleSave}>
            {editingId ? 'Update' : 'Save'}
          </Button>,
        ]}
      >
        <label>Customer Name</label>
        <Input
          value={customerName}
          onChange={(e) => setCustomerName(e.target.value)}
          placeholder="Enter customer name"
        />

        <label className="mt-2">Address</label>
        <Input.TextArea
          rows={3}
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          placeholder="Enter address"
        />

        <label className="mt-2">City Name</label>
        <Select
          value={cityId}
          onChange={setCityId}
          placeholder="Select City"
          style={{ width: '100%' }}
        >
          {cities.map((s) => (
            <Select.Option key={s.id} value={s.id}>
              {s.city_name}
            </Select.Option>
          ))}
        </Select>

        <label className="mt-2">Contact Number</label>
        <Input
          maxLength={10}
          value={contactNo}
          inputMode="numeric"
          onChange={(e) => setContactNo(e.target.value.replace(/\D/g, ''))}
          placeholder="Enter 10-digit contact number"
        />
      </Modal>

      {/* ================= DELETE ================= */}
      {/* <Modal
        title="Confirm Delete"
        open={!!deleteId}
        onCancel={() => setDeleteId(null)}
        onOk={confirmDelete}
        okText="Delete"
        okButtonProps={{ danger: true }}
      >
        Are you sure you want to delete this customer?
      </Modal> */}
    </>
  )
}

export default CustomerMaster
